import * as Sentry from "@sentry/browser";

import { queryClient } from "@/config/query-client";

import { KEY_STORAGE, SESSION_KEY_STORAGE } from "../consts/_key_storage";

/**
 * Event emitted when the server rejects an authenticated request with 401.
 * The auth layer listens for this to sign the user out and prompt re-login,
 * complementing the client-side JWT expiry check in `useAuthAware`.
 */
export const AUTH_UNAUTHORIZED_EVENT = "auth:unauthorized";

type UnauthorizedContext = {
  statusCode: number;
  hasToken: boolean;
  skipUnauthorizedHandling?: boolean;
};

/**
 * Decides whether a failed response should trigger the unauthorized (clean
 * token + re-login) flow. Only a 401 on a token-bearing request qualifies:
 * - non-401 statuses (5xx, 403, …) are not session-fatal;
 * - a 401 without a token is an unauthenticated call (e.g. failed login) and
 *   is left to its own error handler;
 * - `skipUnauthorizedHandling` lets the logout request opt out, since it
 *   already tears down the session.
 */
export function shouldEmitUnauthorized({
  statusCode,
  hasToken,
  skipUnauthorizedHandling,
}: UnauthorizedContext): boolean {
  return statusCode === 401 && hasToken && !skipUnauthorizedHandling;
}

/**
 * Removes the persisted auth token (and derived auth state) from storage and
 * detaches the Sentry user. Safe to call multiple times.
 *
 * Session-scoped state is cleared here too. `sessionStorage` is per-tab rather
 * than per-authentication, so in a SPA it survives a logout followed by a login
 * in the same tab and the next user inherits the previous one's state — the
 * dashboard filters came back after a re-login because of exactly this (BUG-07).
 * Called from `emitUnauthorized` as well, so an expired session resets too.
 *
 * `queryClient.clear()` closes the same gap for React Query's cache: logout
 * never unmounts `QueryClientProvider` (it's a `history.push`, not a reload),
 * and query keys for identity-scoped data (e.g. the candidate-facing vacancy
 * list, gated per-user on `internal_mobility` visibility) carry no auth
 * component — so without this, a synchronous re-render right after logout (or
 * a same-tab re-login as a different user) could paint the previous session's
 * cached, personalized results before the background refetch corrects it. The
 * cache is also persisted to `localStorage` (`providers/Query`), so clearing
 * it here is what keeps that persisted copy from rehydrating stale data too.
 */
export function clearAuthStorage() {
  if (typeof window === "undefined") return;

  Sentry.configureScope((scope) => scope.setUser(null));

  localStorage.removeItem(KEY_STORAGE.TOKEN_KEY);
  localStorage.removeItem(KEY_STORAGE.SELECTED_PROMO);

  Object.values(SESSION_KEY_STORAGE).forEach((key) => {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // Storage blocked by the browser — there is nothing persisted to clear.
    }
  });

  queryClient.clear();
}

/**
 * Clears the auth token and notifies the app that the current session is no
 * longer valid. Called from the request layer when a token-bearing request
 * gets a 401 back from the server.
 */
export function emitUnauthorized() {
  if (typeof window === "undefined") return;

  clearAuthStorage();
  window.dispatchEvent(new CustomEvent(AUTH_UNAUTHORIZED_EVENT));
}

/**
 * Subscribes to unauthorized events. Returns an unsubscribe function.
 */
export function onUnauthorized(handler: () => void) {
  if (typeof window === "undefined") return () => undefined;

  window.addEventListener(AUTH_UNAUTHORIZED_EVENT, handler);
  return () => window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, handler);
}
